import React, { useState } from 'react';
import { Play, ArrowRight, Eye, Calendar, Users, BarChart3, CreditCard, Settings, Zap, Target, Globe, FileText, DollarSign, TrendingUp, Package, Receipt, UserCog } from 'lucide-react';

const DemoPage: React.FC = () => {
  const [activeDemo, setActiveDemo] = useState('website');
  const [activeDemoType, setActiveDemoType] = useState('website'); // 'website' or 'admin'

  const websiteDemoSections = [
    { id: 'website', name: 'Homepage', icon: Globe, url: 'https://business-management-frontend.pages.dev/' },
    { id: 'about', name: 'About Page', icon: FileText, url: 'https://business-management-frontend.pages.dev/about/' },
    { id: 'services', name: 'Services Page', icon: Target, url: 'https://business-management-frontend.pages.dev/services/' },
    { id: 'professionals', name: 'Professionals', icon: Users, url: 'https://business-management-frontend.pages.dev/professionals/' },
    { id: 'appointments', name: 'Appointments', icon: Calendar, url: 'https://business-management-frontend.pages.dev/bookings/' },
    { id: 'contact', name: 'Contact Page', icon: Settings, url: 'https://business-management-frontend.pages.dev/contact/' }
  ];

  const adminDemoSections = [
    { id: 'dashboard', name: 'Dashboard', icon: BarChart3, image: '/assets/images/screenshots/dashboard-demo.png' },
    { id: 'bookings', name: 'Bookings', icon: Calendar, image: '/assets/images/screenshots/bookings-demo.png' },
    { id: 'clients', name: 'Clients', icon: Users, image: '/assets/images/screenshots/clients-demo.png' },
    { id: 'documents', name: 'Documents', icon: FileText, image: '/assets/images/screenshots/documents-demo.png' },
    { id: 'team', name: 'Team', icon: UserCog, image: '/assets/images/screenshots/team-demo.png' },
    { id: 'scheduling', name: 'Scheduling', icon: Calendar, image: '/assets/images/screenshots/scheduling-demo.png' },
    { id: 'services', name: 'Services', icon: Target, image: '/assets/images/screenshots/services-demo.png' },
    { id: 'inventory', name: 'Inventory', icon: Package, image: '/assets/images/screenshots/inventory-demo.png' },
    { id: 'invoices', name: 'Invoices', icon: Receipt, image: '/assets/images/screenshots/invoices-demo.png' },
    { id: 'payments', name: 'Payments', icon: DollarSign, image: '/assets/images/screenshots/payments-demo.png' },
    { id: 'revenue', name: 'Revenue', icon: TrendingUp, image: '/assets/images/screenshots/revenue-demo.png' },
    { id: 'reports', name: 'Reports', icon: BarChart3, image: '/assets/images/screenshots/reports-demo.png' },
    { id: 'settings', name: 'Settings', icon: Settings, image: '/assets/images/screenshots/settings-demo.png' }
  ];


  return (
    <div className="min-h-screen bg-gradient-innovation text-white relative overflow-hidden">
      {/* Global Animated Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 to-transparent"></div>
      <div className="absolute top-20 left-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-20 right-10 w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '2s'}}></div>
      <div className="absolute top-1/2 left-1/4 w-64 h-64 bg-purple-500/5 rounded-full blur-2xl animate-pulse" style={{animationDelay: '4s'}}></div>
      <div className="absolute bottom-1/3 right-1/3 w-80 h-80 bg-blue-500/8 rounded-full blur-3xl animate-pulse" style={{animationDelay: '6s'}}></div>
      
      {/* Hero Section */}
      <section className="py-20 relative z-10 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white via-blue-100 to-cyan-300 bg-clip-text text-transparent">
            Experience the Power<br />Before You Buy
          </h1>
          <p className="text-xl md:text-2xl text-slate-300 max-w-4xl mx-auto mb-8 leading-relaxed">
            Take a complete tour of your future platform. See every feature, every interface, 
            and <span className="text-cyan-300 font-semibold">every profit opportunity</span> waiting for you. 
            <span className="text-yellow-300 font-semibold">This could be your business tomorrow</span>.
          </p>
          <div className="mb-8 flex flex-col sm:flex-row gap-4 justify-center">
            <div className="bg-gradient-to-r from-green-500/20 to-emerald-400/20 backdrop-blur-xl border border-green-300/30 text-white px-8 py-4 rounded-2xl text-lg font-bold shadow-2xl hover:shadow-green-500/25 transition-all duration-300 hover:scale-105">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <Eye className="h-5 w-5 text-green-400" />
                Live Interactive Demo
              </div>
            </div>
            <div className="bg-gradient-to-r from-blue-500/20 to-cyan-500/20 backdrop-blur-xl border border-blue-300/30 text-white px-8 py-4 rounded-2xl text-lg font-bold shadow-2xl hover:shadow-blue-500/25 transition-all duration-300 hover:scale-105">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                <Zap className="h-5 w-5 text-blue-400" />
                Instant Access
              </div>
            </div>
            <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 backdrop-blur-xl border border-purple-300/30 text-white px-8 py-4 rounded-2xl text-lg font-bold shadow-2xl hover:shadow-purple-500/25 transition-all duration-300 hover:scale-105">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse"></div>
                <Target className="h-5 w-5 text-purple-400" />
                See Your ROI Potential
              </div>
            </div>
          </div>
          <button 
            onClick={() => document.getElementById('interactive-demo')?.scrollIntoView({ behavior: 'smooth' })}
            className="bg-yellow-400 text-primary-900 px-8 py-4 rounded-lg text-lg font-bold hover:bg-yellow-300 transition-colors duration-200 inline-flex items-center shadow-xl hover:shadow-yellow-500/25 transform hover:scale-105"
          >
            <Play className="mr-2 h-5 w-5" />
            Start Interactive Demo
          </button>
        </div>
      </section>

      {/* Interactive System Demo */}
      <section id="interactive-demo" className="py-20 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Interactive System Demo</h2>
            <p className="text-xl text-slate-300">Explore both sides of your complete business platform</p>
          </div>

          {/* Demo Type Selector */}
          <div className="flex justify-center mb-12">
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-2 border border-white/10">
              <button
                onClick={() => {
                  setActiveDemoType('website');
                  setActiveDemo('website');
                }}
                className={`px-8 py-4 rounded-xl transition-all duration-300 font-semibold ${
                  activeDemoType === 'website'
                    ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg'
                    : 'text-slate-300 hover:text-white hover:bg-white/5'
                }`}
              >
                🌐 Client-Facing Website
              </button>
              <button
                onClick={() => {
                  setActiveDemoType('admin');
                  setActiveDemo('dashboard');
                }}
                className={`px-8 py-4 rounded-xl transition-all duration-300 font-semibold ${
                  activeDemoType === 'admin'
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg'
                    : 'text-slate-300 hover:text-white hover:bg-white/5'
                }`}
              >
                ⚙️ Admin Management System
              </button>
            </div>
          </div>

          {/* Demo Sections */}
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            {(activeDemoType === 'website' ? websiteDemoSections : adminDemoSections).map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveDemo(section.id)}
                className={`flex items-center space-x-2 px-4 py-3 rounded-lg transition-colors duration-200 ${
                  activeDemo === section.id
                    ? activeDemoType === 'website' 
                      ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg'
                      : 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg'
                    : 'bg-white/5 backdrop-blur-sm text-white hover:bg-white/10'
                }`}
              >
                <section.icon className="h-4 w-4" />
                <span className="text-sm font-medium">{section.name}</span>
              </button>
            ))}
          </div>

          {/* Demo Content Area */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl shadow-xl p-8">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-white mb-2">
                {activeDemoType === 'website' ? '🌐 Client Experience' : '⚙️ Admin Dashboard'}
              </h3>
              <p className="text-slate-300">
                {activeDemoType === 'website' 
                  ? 'What your customers see and interact with'
                  : 'Powerful tools to manage your entire business'
                }
              </p>
            </div>
            
            <div className="bg-gray-100 rounded-lg mb-6 overflow-hidden relative" style={{ height: '600px' }}>
              {activeDemoType === 'website' ? (
                <iframe
                  src={websiteDemoSections.find(s => s.id === activeDemo)?.url}
                  className="w-full h-full border-0 rounded-lg"
                  title={`${websiteDemoSections.find(s => s.id === activeDemo)?.name} Live Demo`}
                  loading="lazy"
                  sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-100">
                  <img 
                    src={adminDemoSections.find(s => s.id === activeDemo)?.image}
                    alt={`${adminDemoSections.find(s => s.id === activeDemo)?.name} Demo`}
                    className="w-full h-full object-contain"
                    style={{ display: 'block' }}
                  />
                </div>
              )}
            </div>
            
            {/* Live Website Info */}
            {activeDemoType === 'website' && (
              <div className="text-center mb-8">
                <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 backdrop-blur-xl border border-green-300/30 text-white px-6 py-3 rounded-xl inline-flex items-center">
                  <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse mr-3"></div>
                  <span className="font-semibold">🌐 Live Website - Fully Interactive!</span>
                </div>
                <p className="text-slate-400 text-sm mt-2">
                  Scroll, click, and explore the actual website your clients will use
                </p>
              </div>
            )}
            
            <div className="grid md:grid-cols-3 gap-6">
              {activeDemoType === 'website' ? (
                <>
                  <div className="text-center">
                    <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Globe className="h-6 w-6 text-blue-600" />
                    </div>
                    <h4 className="font-semibold text-white mb-1">Beautiful Design</h4>
                    <p className="text-sm text-slate-300">Modern, responsive website that converts</p>
                  </div>
                  <div className="text-center">
                    <div className="bg-green-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Calendar className="h-6 w-6 text-green-600" />
                    </div>
                    <h4 className="font-semibold text-white mb-1">Easy Booking</h4>
                    <p className="text-sm text-slate-300">Seamless appointment scheduling for clients</p>
                  </div>
                  <div className="text-center">
                    <div className="bg-purple-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Users className="h-6 w-6 text-purple-600" />
                    </div>
                    <h4 className="font-semibold text-white mb-1">Client Portal</h4>
                    <p className="text-sm text-slate-300">Personal dashboard for customer management</p>
                  </div>
                </>
              ) : (
                <>
                  <div className="text-center">
                    <div className="bg-purple-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                      <BarChart3 className="h-6 w-6 text-purple-600" />
                    </div>
                    <h4 className="font-semibold text-white mb-1">Real-time Analytics</h4>
                    <p className="text-sm text-slate-300">Track performance and growth metrics</p>
                  </div>
                  <div className="text-center">
                    <div className="bg-green-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Users className="h-6 w-6 text-green-600" />
                    </div>
                    <h4 className="font-semibold text-white mb-1">Client Management</h4>
                    <p className="text-sm text-slate-300">Complete customer relationship tools</p>
                  </div>
                  <div className="text-center">
                    <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Settings className="h-6 w-6 text-blue-600" />
                    </div>
                    <h4 className="font-semibold text-white mb-1">Full Control</h4>
                    <p className="text-sm text-slate-300">Customize every aspect of your business</p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </section>


      {/* Live Demo CTA */}
      <section className="py-20 relative z-10 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Try the Full Platform?
          </h2>
          <p className="text-xl mb-8 text-blue-100">
            Experience the complete system with full access to all features
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => alert('Live demo would open here. For now, please contact us to schedule a personalized demo!')}
              className="bg-yellow-400 text-primary-900 px-8 py-4 rounded-lg text-lg font-bold hover:bg-yellow-300 transition-colors duration-200 inline-flex items-center justify-center">
              <Play className="mr-2 h-5 w-5" />
              Launch Live Demo
            </button>
            <a
              href="/pricing"
              className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-white/5 backdrop-blur-sm hover:text-cyan-400 transition-colors duration-200 inline-flex items-center justify-center"
            >
              Get Your Platform
              <ArrowRight className="ml-2 h-5 w-5" />
            </a>
          </div>
        </div>
      </section>

      {/* Schedule Personal Demo */}
      <section className="py-16 relative z-10">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-white mb-4">
            Want a Personal Demo?
          </h2>
          <p className="text-slate-300 mb-6">
            Schedule a one-on-one demo call to see how the platform can be customized for your specific needs
          </p>
          <button 
            onClick={() => window.open('mailto:contact@yourdomain.com?subject=Personal Demo Request&body=Hi, I would like to schedule a personal demo of the platform. Please let me know your availability.', '_blank')}
            className="bg-primary-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors duration-200 inline-flex items-center">
            <Calendar className="mr-2 h-5 w-5" />
            Schedule Demo Call
          </button>
        </div>
      </section>
    </div>
  );
};

export default DemoPage;
